import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserDropdown from './UserDropdown';
import LanguageSelector from './LanguageSelector';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  showUserInfo?: boolean;
  variant?: 'default' | 'player' | 'landing';
  onLogin?: () => void;
  onStartTrial?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  showUserInfo = true,
  variant = 'default',
  onLogin,
  onStartTrial,
}) => {
  const { t } = useTranslation('common');
  const { user, loading: isLoadingUser } = useAuth();
  const history = useHistory();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Publish the rendered header height as --header-height so sticky elements
  // (e.g. .category-filter-wrapper, .page padding) dock flush under it
  // regardless of breakpoint, font scaling, or future header content changes.
  useEffect(() => {
    const node = headerRef.current;
    if (!node) return;

    const updateHeight = () => {
      const height = node.getBoundingClientRect().height;
      if (height > 0) {
        document.documentElement.style.setProperty(
          '--header-height',
          `${height}px`,
        );
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [user, isLoadingUser, variant]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const headerClasses = {
    landing: 'header glass fixed',
    default: 'header glass fixed',
    player: 'header player-header',
  };

  return (
    <header
      ref={headerRef}
      className={
        headerClasses[variant]
          ? headerClasses[variant]
          : headerClasses['default']
      }
    >
      <div className='container-mobile '>
        <div className={`flex justify-between items-center`}>
          <div className='flex items-center'>
            <picture>
              <source srcSet='/images/logo.webp' type='image/webp' />
              <img
                onClick={() => history.push('/')}
                src='/images/logo.png'
                alt='Presencia App'
                className='logo h-8 sm:h-10 md:h-12 object-contain cursor-pointer'
                width={220}
                height={31}
              />
            </picture>
          </div>

          {variant === 'landing' ? (
            <>
              <div className='header-desktop-nav flex items-center space-x-4'>
                <LanguageSelector />
                <button
                  onClick={onStartTrial}
                  className='btn btn-secondary small hide-on-mobile'
                >
                  {t('cta.freeTrial')}
                </button>
                <button
                  onClick={onLogin}
                  className='btn btn-primary small black'
                >
                  {t('cta.login')}
                </button>
              </div>
            </>
          ) : (
            showUserInfo &&
            (isLoadingUser ? (
              <div
                className='flex items-center space-x-2 md:space-x-4 gap-4'
                style={{ padding: '8px' }}
              >
                <div
                  className='spinner'
                  style={{
                    width: '24px',
                    height: '24px',
                    borderWidth: '2px',
                  }}
                ></div>
              </div>
            ) : user ? (
              <div className='flex items-center space-x-2 md:space-x-4 gap-4'>
                <LanguageSelector />
                <UserDropdown user={user} />
              </div>
            ) : (
              <div className='header-desktop-nav flex items-center space-x-4'>
                <LanguageSelector />
                <button
                  onClick={() => history.push('/register')}
                  className='btn btn-secondary small hide-on-mobile'
                >
                  {t('cta.freeTrial')}
                </button>
                <button
                  onClick={() => history.push('/login')}
                  className='btn btn-primary small black'
                >
                  {t('cta.login')}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
